import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ExpandedListTab from '../ExpandedListTab';
import SaveButton from '../buttons/SaveButton';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import useExtendedState from '../../hooks/useExtendedState';
import SelectField from '../fields/SelectField';
import NumberField from '../fields/NumberField';
import { ALL_AUCTION_LIMITS_QUERY_NAME, CREATE_AUCTION_LIMIT } from '../../graphql/auctionLimits';
import { auctionLimitPropertiesType } from '../../types';
import useSingleValidator from '../../hooks/useSingleValidator';
import { auctionLimitValueValidator } from '../../validators/auctionLimits';
import Constants from '../../utils/Constants';

AuctionLimitCreateTab.propTypes = {
    auctionLimitProps: auctionLimitPropertiesType.isRequired,
    hideHandler: PropTypes.func.isRequired,
};

function AuctionLimitCreateTab(props) {
    const {
        auctionLimitProps,
        hideHandler,
    } = props;
    const { t } = useTranslation();
    const {
        availableProducts,
        availableMembers,
        minAuctionLimit,
        maxAuctionLimit,
    } = auctionLimitProps;
    const memberIdState = useExtendedState(availableMembers[0].memberId);
    const [memberName, setMemberName] = useState(availableMembers[0].name);
    const onMemberChange = (newMemberId) => {
        setMemberName(availableMembers.find((availableMember) => availableMember.memberId === newMemberId).name);
    };
    const productIdState = useExtendedState(availableProducts[0].productId);
    const [productName, setProductName] = useState(availableProducts[0].name);
    const onProductChange = (newProductId) => {
        setProductName(availableProducts.find((availableProduct) => availableProduct.productId === newProductId).name);
    };
    const limitState = useExtendedState(1);
    const { validator, isValid } = useSingleValidator(true, useMemo(() => auctionLimitValueValidator(t, minAuctionLimit, maxAuctionLimit), [t, minAuctionLimit, maxAuctionLimit]));
    const [isDisabled, setIsDisabled] = useState(false);
    isValid()
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const extendedMutation = useExtendedMutation(CREATE_AUCTION_LIMIT, {
        variables: {
            memberId: memberIdState.value,
            productId: productIdState.value,
            limit: limitState.value,
        },
        successCallback: hideHandler,
        successMessageOptions: { key: 'auction-limits.notification.auction-limit-create-success', dataProperty: 'createAuctionLimit' },
        failureMessageOptions: {
            key: 'auction-limits.notification.auction-limit-create-failure',
            params: {
                memberName,
                memberId: memberIdState.value,
                productName,
                productId: productIdState.value,
            },
        },
        refetchQueries: [ALL_AUCTION_LIMITS_QUERY_NAME],
    });

    return (
        <ExpandedListTab
            className="auction-limit-create-tab"
            title={t('auction-limits.auction-limit-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <SelectField
                    label={t('auction-limits.member')}
                    state={memberIdState}
                    options={availableMembers.map((availableMember) => (
                        <option value={availableMember.memberId} key={availableMember.memberId}>
                            {availableMember.name} ({availableMember.memberId})
                        </option>
                    ))}
                    onChange={onMemberChange}
                    className="auction-limit-create-tab--fields--member"
                    inputId="auction-limit-create-tab--fields--member"
                    isRequired
                />
                <SelectField
                    label={t('auction-limits.product')}
                    state={productIdState}
                    options={availableProducts.map((availableProduct) => (
                        <option value={availableProduct.productId} key={availableProduct.productId}>
                            {availableProduct.name} ({availableProduct.productId})
                        </option>
                    ))}
                    onChange={onProductChange}
                    className="auction-limit-create-tab--fields--product"
                    inputId="auction-limit-create-tab--fields--product"
                    isRequired
                />
                <NumberField
                    label={t('auction-limits.limit')}
                    className="auction-limit-create-tab--fields--limit"
                    inputId="auction-limit-create-tab--fields--limit"
                    state={limitState}
                    validator={validator}
                    step={Constants.AUCTION_LIMIT_STEP}
                    decrementDisabled={limitState.value - Constants.AUCTION_LIMIT_STEP < minAuctionLimit}
                    incrementDisabled={limitState.value + Constants.AUCTION_LIMIT_STEP > maxAuctionLimit}
                    min={minAuctionLimit}
                    max={maxAuctionLimit}
                    isRequired
                />
            </div>
            <div className="rich-list-row-expanded--buttons">
                <SaveButton
                    extendedMutation={extendedMutation}
                    disabled={isDisabled}
                    confirmationMessage={t('auction-limits.confirm-create-auction-limit', {
                        memberName,
                        memberId: memberIdState.value,
                        productName,
                        productId: productIdState.value,
                    })}
                    extraConfirmationMessage={t('auction-limits.extra-confirm-create-auction-limit', {
                        memberName,
                        memberId: memberIdState.value,
                        productName,
                        productId: productIdState.value,
                    })}
                />
            </div>
        </ExpandedListTab>
    );
}

export default AuctionLimitCreateTab;
